import styled from "@emotion/styled/macro";
import { Typography, tokens } from "@sunrun/experience-ui-components";
import { ReactComponent as SolarHouseIllustration } from "components/assets/images/offer-review-comparison-solar-house.svg";
import { ReactComponent as UtilityHouseIllustration } from "components/assets/images/offer-review-comparison-utility-house.svg";
import { OfferReviewInfoTooltip } from "components/atoms/OfferReviewInfoTooltip";
import { OFFER_REVIEW_COMPARISON_GREEN } from "constants/offerReviewComparisonColors";

type HouseVariant = "utility" | "solar";

type OfferReviewComparisonHouseIllustrationProps = {
  variant: HouseVariant;
  sunrunPercent: number;
};

const UTILITY_PERCENT_TOOLTIP = (
  <>
    Utility % represents the estimated percent of current usage that will be
    supplied by the utility. Actual energy from the utility will vary based on
    your usage patterns in the future.
    <br />
    <br />
    This estimate will be finalized for you to approve after engineering review,
    and may be adjusted based on the final system design.
  </>
);

const SUNRUN_PERCENT_TOOLTIP = (
  <>
    Sunrun % represents the estimated percent of current usage that will be
    supplied by the Sunrun system. Actual energy from Sunrun will vary based on
    your usage patterns in the future.
    <br />
    <br />
    This estimate will be finalized for you to approve after engineering review,
    and may be adjusted based on the final system design.
  </>
);

type HouseNumberBadgePosition = {
  number: number;
  top: string;
  left: string;
};

const HOUSE_NUMBER_BADGES: Record<HouseVariant, HouseNumberBadgePosition[]> = {
  utility: [{ number: 1, top: "80%", left: "86.5%" }],
  solar: [
    { number: 1, top: "42%", left: "17%" },
    { number: 2, top: "82%", left: "50%" },
    { number: 3, top: "80%", left: "86.5%" },
  ],
};

const clampHousePercent = (value: number) =>
  Math.min(100, Math.max(0, Math.floor(value)));

const OfferReviewComparisonHouseIllustration = ({
  variant,
  sunrunPercent,
}: OfferReviewComparisonHouseIllustrationProps) => {
  const isUtility = variant === "utility";
  const sunrun = clampHousePercent(sunrunPercent);
  const utility = 100 - sunrun;
  const showSunrunLine = !isUtility && sunrun > 0;

  return (
    <IllustrationRoot
      $maxWidth={isUtility ? 412 : 410}
      $padTop={isUtility ? 63 : 0}
    >
      <SvgFrame>
        {isUtility ? (
          <UtilityHouseSvg aria-hidden />
        ) : (
          <SolarHouseSvg aria-hidden />
        )}
        {showSunrunLine ? (
          <GreenLineTrack aria-hidden>
            <GreenLineFill />
          </GreenLineTrack>
        ) : null}
        <PoleLabels $variant={variant}>
          <PercentRow>
            <HouseLabel size={16} fontWeight="bold" color={tokens.BLACK}>
              {utility}%
            </HouseLabel>
            <OfferReviewInfoTooltip
              content={UTILITY_PERCENT_TOOLTIP}
              ariaLabel="About the utility percent"
              testId={`house-utility-percent-tooltip-${variant}`}
            />
          </PercentRow>
          <HouseLabel size={16} fontWeight="bold" color={tokens.BLACK}>
            Utility
          </HouseLabel>
        </PoleLabels>
        {showSunrunLine ? (
          <SunrunLineLabels>
            <PercentRow>
              <HouseLabel size={16} fontWeight="bold" color={tokens.BLACK}>
                {sunrun}%
              </HouseLabel>
              <OfferReviewInfoTooltip
                content={SUNRUN_PERCENT_TOOLTIP}
                ariaLabel="About the Sunrun percent"
                testId="house-sunrun-percent-tooltip"
              />
            </PercentRow>
            <HouseLabel size={16} fontWeight="bold" color={tokens.BLACK}>
              Sunrun
            </HouseLabel>
          </SunrunLineLabels>
        ) : null}
        {HOUSE_NUMBER_BADGES[variant].map(({ number, top, left }) => (
          <NumberBadge key={number} $top={top} $left={left} aria-hidden>
            <NumberBadgeLabel>{number}</NumberBadgeLabel>
          </NumberBadge>
        ))}
      </SvgFrame>
    </IllustrationRoot>
  );
};

const IllustrationRoot = styled.div<{ $maxWidth: number; $padTop: number }>`
  width: 100%;
  max-width: ${({ $maxWidth }) => $maxWidth}px;
  margin: 0 auto;
  padding-top: ${({ $padTop }) => $padTop}px;
  box-sizing: content-box;
`;

const SvgFrame = styled.div`
  position: relative;
  width: 100%;
`;

const houseSvgStyles = `
  display: block;
  width: 100%;
  height: auto;

  [data-offer-review-house-labels="true"],
  [data-offer-review-house-sunrun-labels="true"] {
    display: none;
  }

  [data-offer-review-house-green-line="true"] {
    display: none;
  }
`;

const UtilityHouseSvg = styled(UtilityHouseIllustration)`
  ${houseSvgStyles}
`;

const SolarHouseSvg = styled(SolarHouseIllustration)`
  ${houseSvgStyles}
`;

const GreenLineTrack = styled.div`
  position: absolute;
  left: 33.5%;
  top: 2.3%;
  width: 0.37%;
  height: 31%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  pointer-events: none;
`;

const GreenLineFill = styled.div`
  width: 100%;
  height: 100%;
  min-height: 2px;
  background: ${OFFER_REVIEW_COMPARISON_GREEN};
  border-radius: 1px;
`;

const HouseLabel = styled(Typography)`
  && {
    margin: 0;
    line-height: 1.15;
  }
`;

const PoleLabels = styled.div<{ $variant: HouseVariant }>`
  position: absolute;
  right: ${({ $variant }) => ($variant === "utility" ? "2%" : "1.5%")};
  top: ${({ $variant }) => ($variant === "utility" ? "0%" : "24%")};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  pointer-events: none;
`;

const PercentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SunrunLineLabels = styled.div`
  position: absolute;
  left: 36%;
  top: 2%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  pointer-events: none;
`;

const NumberBadge = styled.div<{ $top: string; $left: string }>`
  position: absolute;
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: color-mix(in srgb, ${tokens.BLACK} 10%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const NumberBadgeLabel = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${tokens.TINTS_HERO_BLUE_90};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.12px;
  color: ${tokens.BLACK};
`;

export { OfferReviewComparisonHouseIllustration };
